// The chat dock's slide-up panel: transcript, composer, proposal review, and
// the admin debug strip (Task 4.2).
//
// Escape steps back one level per press (site-assistant SA25): close history,
// then restore compact size if maximized, then close the panel. It never clears
// the composer - a textarea of text the user wrote (ui-ux-standard rule 1) - and
// the draft itself lives in AiChatDock, so closing the panel does not lose it.
// The panel stops each Escape it handles so document-level listeners (checklist
// multi-select, the account menu) do not also act on the same press.

import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUp, Copy, History, Maximize2, Minimize2, Plus, Square, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { formatInstant, resolveDisplayZone, type AiAssistantStatus, type AiChatContextRef, type AiChatSessionSummary, type AiProposalOp, type AiTicketDraft, type AiUndoResult, type AiUndoView } from "@checklist/shared";
import {
  ApiError,
  deleteChatSession,
  fetchAiKbSearch,
  fetchAiKbSection,
  fetchChatSession,
  listChatSessions,
  postAiOutcome,
  redoProposal,
  undoProposal,
  type AiKbSearchResult,
  type AiKbSection,
  type ApiApplyProposalResult,
} from "../../lib/api.js";
import { showToast } from "../ui/Toast.js";
import { useConfirm } from "../ui/ConfirmDialog.js";
import { SearchInput } from "../ui/SearchInput.js";
import { streamChat, type StreamChatHandle } from "../../lib/aiChat/client.js";
import { degradedReasonForErrorCode, degradedSentence, transcriptNotice, type ClientDegradedReason } from "../../lib/aiChat/status.js";
import { AiDebugStrip, type AiDebugInfo } from "./AiDebugStrip.js";
import { ProposalCard } from "./ProposalCard.js";
import { RemoveRowButton } from "../ui/RemoveRowButton.js";
import { ReportProblemDialog } from "../feedback/ReportProblemDialog.js";
import type { SurfaceId } from "../../lib/feedback/surfaces.js";
import { ticketBody } from "../../lib/aiChat/ticket.js";
import { copyReply } from "../../lib/aiChat/copyReply.js";
import { STALL_MS, WATCHDOG_MESSAGE, WATCHDOG_MS, waitingLabel } from "../../lib/aiChat/waiting.js";

// The markdown renderer (site-assistant SA22) is its own chunk, so pages whose
// users never open the assistant do not download it. Mounting the panel starts
// the download; until it lands a reply shows as plain text.
const loadAiMarkdown = () => import("./AiMarkdown.js");
const AiMarkdown = lazy(loadAiMarkdown);

function ReplyMarkdown({ text }: { text: string }) {
  return (
    <Suspense fallback={<p className="whitespace-pre-wrap">{text}</p>}>
      <AiMarkdown text={text} />
    </Suspense>
  );
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Persisted assistant TURN seq, from the "done" event (Task 4.3). Live-session messages only. */
  seq?: number;
  rating?: "up" | "down";
  /** navigate_to / draft_ticket cards emitted while this message was streamed (Task 6.1, F-10/F-12). */
  cards?: ChatCard[];
  /** The user pressed Stop; whatever text had arrived is kept (site-assistant SA24). */
  stopped?: boolean;
  /** Admin-only diagnostics for this turn (site-assistant SA23). Live-session messages only. */
  debug?: AiDebugInfo;
}

type ChatCard =
  | { type: "link"; route: string; label: string }
  | { type: "ticket"; cardId: string; draft: AiTicketDraft; conversationRef: string };

interface ActiveProposal {
  proposalId: string;
  summary: string;
  ops: AiProposalOp[];
}

function storageKeyFor(context: AiChatContextRef | null): string {
  return context ? `aiChatSession:${context.kind}:${context.id}` : "aiChatSession:global";
}

function readStoredSessionId(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredSessionId(key: string, sessionId: string) {
  try {
    sessionStorage.setItem(key, sessionId);
  } catch {
    // Private mode / quota: the session still works for this turn, only
    // cross-reopen resume via sessionStorage is lost.
  }
}

/** Empty string means "start a new chat" even after the panel is closed. */
const NEW_SESSION_MARKER = "";

/** Keep enough room for a considered prompt without letting the composer take over the chat. */
const COMPOSER_MAX_LINES = 6;

// ---- Drag-to-resize (compact size only; maximized uses a fixed inset) ----
// Handles are hidden below `sm` — on a narrow/mobile viewport the panel is
// already close to full-width and touch-dragging a 6px edge is unreliable, so
// resizing is a desktop-only affordance there too.

const PANEL_SIZE_STORAGE_KEY = "aiChatPanelSize";
const PANEL_MIN_WIDTH = 320;
const PANEL_MIN_HEIGHT = 360;
const PANEL_DEFAULT_WIDTH = 384;
const PANEL_DEFAULT_HEIGHT = 512;
/** Matches the panel's `right-6` anchor, plus a small clearance on the growing (left) side. */
const PANEL_RIGHT_OFFSET = 24;
const PANEL_LEFT_CLEARANCE = 16;
/** Matches the panel's `bottom-24` anchor, plus a small clearance on the growing (top) side. */
const PANEL_BOTTOM_OFFSET = 96;
const PANEL_TOP_CLEARANCE = 16;

interface PanelSize {
  width: number;
  height: number;
}

function clampPanelSize(size: PanelSize): PanelSize {
  const maxWidth = Math.max(PANEL_MIN_WIDTH, window.innerWidth - PANEL_RIGHT_OFFSET - PANEL_LEFT_CLEARANCE);
  const maxHeight = Math.max(PANEL_MIN_HEIGHT, window.innerHeight - PANEL_BOTTOM_OFFSET - PANEL_TOP_CLEARANCE);
  return {
    width: Math.min(Math.max(size.width, PANEL_MIN_WIDTH), maxWidth),
    height: Math.min(Math.max(size.height, PANEL_MIN_HEIGHT), maxHeight),
  };
}

function readStoredPanelSize(): PanelSize | null {
  try {
    const raw = localStorage.getItem(PANEL_SIZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PanelSize>;
    if (typeof parsed.width === "number" && typeof parsed.height === "number") {
      return { width: parsed.width, height: parsed.height };
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStoredPanelSize(size: PanelSize) {
  try {
    localStorage.setItem(PANEL_SIZE_STORAGE_KEY, JSON.stringify(size));
  } catch {
    // ignore
  }
}

type ResizeEdge = "left" | "top" | "corner";

interface ResizeDrag {
  edge: ResizeEdge;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

/**
 * The waiting indicator (site-assistant SA24): bouncing dots (a pulse under
 * reduced motion - index.css) plus a visible label that changes with real
 * events. The label is what screen readers hear, once per change.
 */
function TypingDots({ label }: { label: string }) {
  return (
    <div
      className="mr-6 inline-flex w-fit items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-muted"
      role="status"
      aria-live="polite"
    >
      <span className="flex items-center gap-1" aria-hidden="true">
        <span className="ai-typing-dot" />
        <span className="ai-typing-dot" />
        <span className="ai-typing-dot" />
      </span>
      <span className="text-xs">{label}</span>
    </div>
  );
}

function ChatCardView({
  card,
  submitted,
  onReviewTicket,
}: {
  card: ChatCard;
  submitted: boolean;
  onReviewTicket: (card: Extract<ChatCard, { type: "ticket" }>) => void;
}) {
  if (card.type === "link") {
    return (
      <Link
        to={card.route}
        className="mr-6 mt-2 inline-flex w-fit items-center rounded-lg border border-line bg-white px-2.5 py-1 text-xs font-medium text-brand-700 no-underline hover:underline"
      >
        {card.label}
      </Link>
    );
  }
  return (
    <div className="mr-6 mt-2 max-w-xs rounded-xl border border-line bg-white p-3 shadow-sm">
      <p className="text-sm font-medium text-ink">{card.draft.title}</p>
      {submitted ? (
        <p className="mt-2 text-xs font-medium text-success">Submitted</p>
      ) : (
        <button
          type="button"
          onClick={() => onReviewTicket(card)}
          className="mt-2 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-700"
        >
          Review and submit
        </button>
      )}
    </div>
  );
}

function formatSessionWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return "Just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  return formatInstant(d, resolveDisplayZone());
}

function sessionTitle(s: AiChatSessionSummary): string {
  const title = s.title.trim();
  return title || "Untitled chat";
}

function sessionScope(s: AiChatSessionSummary): string {
  if (s.resourceKind === "list") return "List";
  if (s.resourceKind === "template") return "Template";
  if (s.resourceKind === "checklist") return "Checklist";
  return "General";
}

function formatAiChatTranscript(input: {
  context: AiChatContextRef | null;
  sessionId: string | null;
  messages: ChatMessage[];
  streamingText: string;
  error: string | null;
  proposal: ActiveProposal | null;
  undo: AiUndoView | null;
}): string {
  const lines: string[] = [
    "NeatTick AI assistant transcript",
    `Copied at: ${new Date().toISOString()}`,
  ];
  if (input.context) {
    lines.push(`Open resource: ${input.context.kind} ${input.context.id}`);
  } else {
    lines.push("Open resource: (none)");
  }
  if (input.sessionId) lines.push(`Session: ${input.sessionId}`);
  lines.push("");

  for (const m of input.messages) {
    lines.push(m.role === "user" ? "User:" : "Assistant:");
    lines.push(m.text);
    if (m.stopped) lines.push("(stopped)");
    if (m.debug) lines.push(`Debug: ${JSON.stringify(m.debug)}`);
    lines.push("");
  }
  if (input.streamingText) {
    lines.push("Assistant (in progress):");
    lines.push(input.streamingText);
    lines.push("");
  }
  if (input.error) {
    lines.push(`Error: ${input.error}`);
    lines.push("");
  }
  if (input.proposal) {
    lines.push("--- Proposal (pending review) ---");
    lines.push(`proposalId: ${input.proposal.proposalId}`);
    lines.push(`summary: ${input.proposal.summary}`);
    lines.push(JSON.stringify(input.proposal.ops, null, 2));
    lines.push("");
  }
  if (input.undo) {
    lines.push(`--- Proposal (${input.undo.state}) ---`);
    lines.push(`proposalId: ${input.undo.proposalId}`);
    lines.push(`summary: ${input.undo.summary}`);
    lines.push(`appliedAt: ${input.undo.appliedAt}`);
    lines.push(JSON.stringify(input.undo.ops, null, 2));
    lines.push("");
  }
  return lines.join("\n").trim() + "\n";
}

export function AiChatPanel({
  context,
  status,
  draft: composerValue,
  onDraftChange: setComposerValue,
  onClose,
}: {
  context: AiChatContextRef | null;
  status: AiAssistantStatus;
  /** The composer text, owned by AiChatDock so it outlives the panel (site-assistant SA25). */
  draft: string;
  onDraftChange: (value: string) => void;
  onClose: () => void;
}) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [forceNew, setForceNew] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState("");
  /**
   * Non-null while a turn is in flight and nothing is visibly arriving: before the
   * first delta, and during every tool round (site-assistant SA24). `tool` is the
   * tool now running, if any. Null while text streams - the text is the progress.
   */
  const [pending, setPending] = useState<{ tool: string | null } | null>(null);
  /** Bumped on every stream event; the stall timer restarts on it. */
  const [eventCount, setEventCount] = useState(0);
  const [stalled, setStalled] = useState(false);
  const [pendingProposal, setPendingProposal] = useState<ActiveProposal | null>(null);
  const [undo, setUndo] = useState<AiUndoView | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [degradedReason, setDegradedReason] = useState<ClientDegradedReason | null>(null);
  const [turnLimitHit, setTurnLimitHit] = useState(false);
  const [reportDialog, setReportDialog] = useState<{
    surfaceId: SurfaceId;
    payload: Record<string, unknown>;
    initialType?: "bug" | "feature" | "contact";
    prefillBody?: string;
    onSubmitted?: (ref: string) => void;
  } | null>(null);
  const [liveCards, setLiveCards] = useState<ChatCard[]>([]);
  const [submittedTicketIds, setSubmittedTicketIds] = useState<Set<string>>(new Set());
  const [kbQuery, setKbQuery] = useState("");
  const [kbResults, setKbResults] = useState<AiKbSearchResult[] | null>(null);
  const [kbSection, setKbSection] = useState<AiKbSection | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [panelSize, setPanelSize] = useState<PanelSize>(() =>
    clampPanelSize(readStoredPanelSize() ?? { width: PANEL_DEFAULT_WIDTH, height: PANEL_DEFAULT_HEIGHT })
  );
  const [resizing, setResizing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<AiChatSessionSummary[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const streamingTextRef = useRef("");
  const liveCardsRef = useRef<ChatCard[]>([]);
  const handleRef = useRef<StreamChatHandle | null>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const historyMenuRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const resizeDragRef = useRef<ResizeDrag | null>(null);
  /** The in-flight turn's debug event; attached to its reply when the turn finalizes (SA23). */
  const debugRef = useRef<AiDebugInfo | null>(null);
  const watchdogRef = useRef<number | null>(null);

  function clearWatchdog() {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }

  // The "still working" label (SA24): restarts on every stream event.
  useEffect(() => {
    setStalled(false);
    if (!isStreaming) return;
    const timer = window.setTimeout(() => setStalled(true), STALL_MS);
    return () => window.clearTimeout(timer);
  }, [isStreaming, eventCount]);

  // Keep the compact size on-screen across viewport/orientation changes (only
  // shrinks toward the new max; never grows past what the user chose).
  useEffect(() => {
    function onWindowResize() {
      setPanelSize((size) => clampPanelSize(size));
    }
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  useEffect(() => {
    if (!resizing) return;

    function onPointerMove(e: PointerEvent) {
      const drag = resizeDragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      setPanelSize(
        clampPanelSize({
          width: drag.edge === "top" ? drag.startWidth : drag.startWidth - dx,
          height: drag.edge === "left" ? drag.startHeight : drag.startHeight - dy,
        })
      );
    }
    function onPointerUp() {
      resizeDragRef.current = null;
      setResizing(false);
      setPanelSize((size) => {
        writeStoredPanelSize(size);
        return size;
      });
    }
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [resizing]);

  function startResize(edge: ResizeEdge) {
    return (e: React.PointerEvent) => {
      if (maximized || e.button !== 0) return;
      e.preventDefault();
      resizeDragRef.current = {
        edge,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: panelSize.width,
        startHeight: panelSize.height,
      };
      setResizing(true);
    };
  }

  // A textarea's scrollHeight includes wrapped lines as well as explicit newlines,
  // which gives the composer the familiar chat-style growth behavior.
  useLayoutEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;

    composer.style.height = "auto";
    const styles = window.getComputedStyle(composer);
    const lineHeight = Number.parseFloat(styles.lineHeight);
    const verticalPadding =
      Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
    const verticalBorder =
      Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth);
    const maxHeight = lineHeight * COMPOSER_MAX_LINES + verticalPadding + verticalBorder;
    const nextHeight = Math.min(composer.scrollHeight, maxHeight);

    composer.style.height = `${nextHeight}px`;
    composer.style.overflowY = composer.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [composerValue]);

  function resetTranscript() {
    handleRef.current?.abort();
    handleRef.current = null;
    clearWatchdog();
    setMessages([]);
    setStreamingText("");
    streamingTextRef.current = "";
    setLiveCards([]);
    liveCardsRef.current = [];
    setPendingProposal(null);
    setUndo(null);
    debugRef.current = null;
    setError(null);
    setDegradedReason(null);
    setTurnLimitHit(false);
    setPending(null);
    setIsStreaming(false);
    setKbQuery("");
    setKbResults(null);
    setKbSection(null);
  }

  async function runKbSearch(q: string) {
    const query = q.trim();
    setKbSection(null);
    if (!query) {
      setKbResults(null);
      return;
    }
    try {
      setKbResults(await fetchAiKbSearch(query));
    } catch {
      setKbResults([]);
    }
  }

  async function openKbSection(id: string) {
    try {
      setKbSection(await fetchAiKbSection(id));
    } catch {
      showToast("Could not load that help section.", "error");
    }
  }

  // Reset and rehydrate whenever the panel switches to a different resource.
  useEffect(() => {
    resetTranscript();
    setHistoryOpen(false);

    const key = storageKeyFor(context);
    const stored = readStoredSessionId(key);
    if (stored === NEW_SESSION_MARKER) {
      setSessionId(null);
      setForceNew(true);
      return;
    }
    setForceNew(false);
    setSessionId(stored);
    if (!stored) return;

    let cancelled = false;
    void (async () => {
      try {
        const session = await fetchChatSession(stored);
        if (cancelled) return;
        setMessages(session.turns.map((t) => ({ id: String(t.seq), role: t.role, text: t.text })));
        setUndo(session.undo ?? null);
      } catch {
        if (cancelled) return;
        try {
          sessionStorage.removeItem(key);
        } catch {
          // ignore
        }
        setSessionId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [context?.kind, context?.id]);

  useEffect(() => {
    void loadAiMarkdown();
    return () => {
      handleRef.current?.abort();
      clearWatchdog();
    };
  }, []);

  // The panel is mounted only when the dock opens it, so move focus to the
  // composer after it has been added to the document. This lets people start
  // typing immediately after opening the assistant.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => composerRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [messages, streamingText, liveCards, pendingProposal, undo, pending]);

  useEffect(() => {
    if (!historyOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (historyMenuRef.current?.contains(e.target as Node)) return;
      setHistoryOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [historyOpen]);

  async function refreshHistory() {
    setHistoryError(null);
    try {
      setHistory(await listChatSessions());
    } catch {
      setHistoryError("Could not load previous chats.");
    }
  }

  function handleNewChat() {
    if (isStreaming) return;
    const key = storageKeyFor(context);
    resetTranscript();
    setSessionId(null);
    setForceNew(true);
    writeStoredSessionId(key, NEW_SESSION_MARKER);
    setHistoryOpen(false);
  }

  async function handleLoadSession(id: string) {
    if (isStreaming || id === sessionId) {
      setHistoryOpen(false);
      return;
    }
    const key = storageKeyFor(context);
    resetTranscript();
    setForceNew(false);
    setSessionId(id);
    writeStoredSessionId(key, id);
    setHistoryOpen(false);
    try {
      const session = await fetchChatSession(id);
      setMessages(session.turns.map((t) => ({ id: String(t.seq), role: t.role, text: t.text })));
      setUndo(session.undo ?? null);
    } catch {
      setError("Could not load that conversation.");
      setSessionId(null);
    }
  }

  async function handleDeleteSession(id: string) {
    if (isStreaming || deletingId) return;
    setDeletingId(id);
    try {
      await deleteChatSession(id);
      setHistory((prev) => (prev ?? []).filter((s) => s.sessionId !== id));
      if (sessionId === id) {
        const key = storageKeyFor(context);
        resetTranscript();
        setSessionId(null);
        setForceNew(true);
        writeStoredSessionId(key, NEW_SESSION_MARKER);
      }
    } catch {
      showToast("Could not delete that chat.", "error");
    } finally {
      setDeletingId(null);
    }
  }

  function handleToggleHistory() {
    if (isStreaming) return;
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) void refreshHistory();
  }

  function finalizeStream(assistantSeq?: number, options: { stopped?: boolean } = {}) {
    const finalText = streamingTextRef.current;
    const finalCards = liveCardsRef.current;
    const finalDebug = debugRef.current;
    debugRef.current = null;
    clearWatchdog();
    setIsStreaming(false);
    setPending(null);
    if (finalText || finalCards.length > 0 || options.stopped) {
      setMessages((m) => [
        ...m,
        {
          id: `a${Date.now()}`,
          role: "assistant",
          text: finalText,
          seq: assistantSeq,
          cards: finalCards.length > 0 ? finalCards : undefined,
          stopped: options.stopped || undefined,
          debug: finalDebug ?? undefined,
        },
      ]);
    }
    setStreamingText("");
    streamingTextRef.current = "";
    setLiveCards([]);
    liveCardsRef.current = [];
    handleRef.current = null;
  }

  function handleSend() {
    const text = composerValue.trim();
    if (!text || isStreaming) return;
    setComposerValue("");
    setError(null);
    setPendingProposal(null);
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", text }]);
    setIsStreaming(true);
    setStreamingText("");
    streamingTextRef.current = "";
    setLiveCards([]);
    liveCardsRef.current = [];
    setPending({ tool: null });
    debugRef.current = null;

    const key = storageKeyFor(context);
    // A margin past the chat Lambda's timeout: the wait always ends (SA24). Armed
    // before streamChat, which can finish synchronously (not configured, signed
    // out) - finalizeStream then clears it rather than it firing later.
    clearWatchdog();
    watchdogRef.current = window.setTimeout(() => {
      watchdogRef.current = null;
      handleRef.current?.abort();
      finalizeStream();
      setError(WATCHDOG_MESSAGE);
    }, WATCHDOG_MS);
    handleRef.current = streamChat(
      {
        sessionId: sessionId ?? undefined,
        forceNew: forceNew && !sessionId,
        message: text,
        context,
      },
      (event) => {
        setEventCount((n) => n + 1);
        switch (event.type) {
          case "session":
            setSessionId(event.sessionId);
            setForceNew(false);
            writeStoredSessionId(key, event.sessionId);
            break;
          case "delta":
            streamingTextRef.current += event.text;
            setStreamingText(streamingTextRef.current);
            setPending(null);
            break;
          case "tool":
            // "done" still leaves the turn pending: the model's next round has not produced text yet.
            setPending({ tool: event.status === "running" ? event.name : null });
            break;
          case "proposal":
            setPendingProposal({ proposalId: event.proposalId, summary: event.summary, ops: event.ops });
            break;
          case "link":
            liveCardsRef.current = [...liveCardsRef.current, { type: "link", route: event.route, label: event.label }];
            setLiveCards(liveCardsRef.current);
            break;
          case "ticket":
            liveCardsRef.current = [
              ...liveCardsRef.current,
              { type: "ticket", cardId: event.cardId, draft: event.draft, conversationRef: event.conversationRef },
            ];
            setLiveCards(liveCardsRef.current);
            break;
          case "debug":
            debugRef.current = event;
            break;
          case "error": {
            if (event.code === "TURN_LIMIT") {
              setTurnLimitHit(true);
            } else {
              const reason = degradedReasonForErrorCode(event.code);
              if (reason) setDegradedReason(reason);
              else setError(event.message);
            }
            finalizeStream();
            break;
          }
          case "done":
            finalizeStream(event.assistantSeq);
            if (historyOpen) void refreshHistory();
            break;
        }
      }
    );
  }

  /** Stop ends the wait in the browser; the Lambda still finishes and bills the turn (SA24). */
  function handleAbort() {
    handleRef.current?.abort();
    finalizeStream(undefined, { stopped: true });
  }

  /** Enter sends, Shift+Enter is a newline, neither mid-IME-composition. No Escape branch (SA25). */
  function handleComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  }

  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Escape" || e.nativeEvent.isComposing) return;
    // An Escape from inside a modal (the report-a-problem dialog) belongs to that modal.
    const target = e.target as Element;
    if (!e.currentTarget.contains(target) || target.closest('[aria-modal="true"]')) return;
    e.stopPropagation();
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    if (maximized) {
      setMaximized(false);
      return;
    }
    onClose();
  }

  async function handleProposalApplied(result: ApiApplyProposalResult, selectedCount: number) {
    setPendingProposal(null);
    if (sessionId) {
      try {
        const session = await fetchChatSession(sessionId);
        setUndo(session.undo ?? null);
      } catch {
        showToast("Changes were applied, but Undo could not be loaded.", "error");
      }
    }
    const touchesOpenResource =
      context !== null &&
      result.results.some(
        (r) =>
          r.status === "applied" && r.resourceKind === context.kind && r.resourceId === context.id
      );
    if (touchesOpenResource) {
      window.location.reload();
      return;
    }
    showToast(`Applied ${selectedCount} selected change${selectedCount === 1 ? "" : "s"}.`);
  }

  function handleProposalDiscarded() {
    setPendingProposal(null);
    showToast("Suggestion discarded.");
  }

  const hasTranscript =
    messages.length > 0 || Boolean(streamingText) || Boolean(error) || Boolean(pendingProposal) || Boolean(undo);

  async function handleCopyConversation() {
    if (!hasTranscript) return;
    const text = formatAiChatTranscript({
      context,
      sessionId,
      messages,
      streamingText,
      error,
      proposal: pendingProposal,
      undo,
    });
    try {
      await navigator.clipboard.writeText(text);
      showToast("Conversation copied.");
    } catch {
      showToast("Could not copy the conversation.", "error");
    }
  }

  /** A reply copies as rendered HTML plus clean plain text (SA22); a user message copies as typed. */
  async function handleCopyMessage(message: ChatMessage, trigger: HTMLElement) {
    try {
      if (message.role === "assistant") {
        const body = trigger.closest("[data-reply]")?.querySelector<HTMLElement>("[data-reply-body]") ?? null;
        await copyReply(message.text, body);
        showToast("Reply copied.");
      } else {
        await navigator.clipboard.writeText(message.text);
        showToast("Message copied.");
      }
    } catch {
      showToast("Could not copy the message.", "error");
    }
  }

  function handleRate(message: ChatMessage, rating: "up" | "down") {
    if (message.seq === undefined || message.rating || !sessionId) return;
    const seq = message.seq;
    setMessages((m) => m.map((msg) => (msg.id === message.id ? { ...msg, rating } : msg)));
    void postAiOutcome({ event: rating === "up" ? "thumbs_up" : "thumbs_down", sessionId, seq });
    if (rating === "down") {
      setReportDialog({
        surfaceId: "assistant.member.reply",
        payload: { assistantProfile: "member", conversationRef: sessionId },
      });
    }
  }

  function handleReviewTicket(card: Extract<ChatCard, { type: "ticket" }>) {
    setReportDialog({
      surfaceId: "assistant.member.ticket",
      payload: { assistantProfile: "member", conversationRef: card.conversationRef },
      initialType: card.draft.type,
      prefillBody: ticketBody(card.draft),
      onSubmitted: () => {
        void postAiOutcome({ event: "ticket_submitted", sessionId: card.conversationRef });
        setSubmittedTicketIds((s) => new Set(s).add(card.cardId));
      },
    });
  }

  function confirmMessage(action: "undo" | "redo", details: { dirty: boolean; created: AiUndoView["created"] }): string {
    if (action === "redo") return "This will put the AI's last change back. Edits made since the undo will be lost. Continue?";
    const created = details.created;
    const counts = ["list", "template", "checklist"].map((kind) => {
      const count = created.filter((resource) => resource.kind === kind).length;
      return count ? `${count} ${kind}${count === 1 ? "" : "s"}` : "";
    }).filter(Boolean);
    return [
      "This will restore the AI's last change to how it was before.",
      ...(details.dirty ? ["Edits made since then will be lost."] : []),
      ...(counts.length ? [`This will also delete ${counts.join(", ")} the AI created.`] : []),
      "Continue?",
    ].join("\n");
  }

  async function handleUndoRedo(action: "undo" | "redo") {
    if (!undo) return;
    const run = (confirm: boolean) => action === "undo"
      ? undoProposal(undo.proposalId, confirm)
      : redoProposal(undo.proposalId, confirm);
    let result: AiUndoResult;
    try {
      result = await run(false);
    } catch (err) {
      if (!(err instanceof ApiError) || err.code !== "UNDO_CONFIRM_REQUIRED") {
        if (err instanceof ApiError && (err.code === "UNDO_EXPIRED" || err.code === "UNDO_NOT_FOUND")) {
          setUndo(null);
          showToast("Undo is no longer available.");
          return;
        }
        throw err;
      }
      const body = err.body as { dirty?: boolean; created?: AiUndoView["created"] };
      const ok = await confirm({
        title: action === "redo" ? "Restore AI changes?" : "Undo AI changes?",
        message: confirmMessage(action, { dirty: body.dirty === true, created: body.created ?? undo.created }),
        confirmLabel: "Continue",
        destructive: true,
      });
      if (!ok) return;
      result = await run(true);
    }

    const openCreated = context && result.created.some((resource) => resource.kind === context.kind && resource.id === context.id);
    if (openCreated) {
      navigate(context.kind === "list" ? "/lists" : context.kind === "template" ? "/templates" : "/checklists");
      return;
    }
    const touchesOpenResource = context && [...result.created, ...result.mutated].some(
      (resource) => resource.kind === context.kind && resource.id === context.id
    );
    if (touchesOpenResource) {
      window.location.reload();
      return;
    }
    setUndo((current) => current ? { ...current, state: result.state, created: result.created, mutated: result.mutated } : current);
    showToast(action === "undo" ? "AI changes undone." : "AI changes restored.");
  }

  const indicatorLabel = waitingLabel(pending, stalled);

  const effectiveDegradedReason: ClientDegradedReason | null =
    degradedReason ?? (status.mode === "degraded" ? status.reason ?? null : null);

  return (
    <div
      role="dialog"
      aria-label="AI assistant"
      onKeyDown={handlePanelKeyDown}
      style={maximized ? undefined : { width: panelSize.width, height: panelSize.height }}
      className={
        maximized
          ? "fixed inset-3 z-40 flex flex-col overflow-visible rounded-2xl border border-line bg-white shadow-xl"
          : "fixed bottom-24 right-6 z-30 flex max-h-[calc(100vh-7rem)] max-w-[calc(100vw-3rem)] flex-col overflow-visible rounded-2xl border border-line bg-white shadow-xl"
      }
    >
      {!maximized ? (
        <>
          <div
            onPointerDown={startResize("top")}
            aria-hidden="true"
            className="absolute -top-1 left-3 right-3 hidden h-2 cursor-ns-resize touch-none sm:block"
          />
          <div
            onPointerDown={startResize("left")}
            aria-hidden="true"
            className="absolute -left-1 top-3 bottom-3 hidden w-2 cursor-ew-resize touch-none sm:block"
          />
          <div
            onPointerDown={startResize("corner")}
            aria-hidden="true"
            className="absolute -left-1 -top-1 hidden h-3 w-3 cursor-nwse-resize touch-none sm:block"
          />
        </>
      ) : null}
      <div className="flex items-center justify-between gap-2 rounded-t-2xl border-b border-line px-4 py-3">
        <span className="text-sm font-semibold text-ink">AI assistant</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="New chat"
            title="New chat"
            disabled={isStreaming}
            onClick={handleNewChat}
            className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus size={16} aria-hidden="true" />
          </button>
          <div className="relative" ref={historyMenuRef}>
            <button
              type="button"
              aria-label="Previous chats"
              title="Previous chats"
              aria-expanded={historyOpen}
              disabled={isStreaming}
              onClick={handleToggleHistory}
              className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <History size={16} aria-hidden="true" />
            </button>
            {historyOpen ? (
              <div
                role="menu"
                aria-label="Previous chats"
                className="absolute right-0 z-10 mt-1 max-h-64 w-72 overflow-hidden rounded-xl border border-line bg-white shadow-lg"
              >
                <div className="max-h-64 overflow-y-auto py-1">
                  {historyError ? (
                    <p className="px-3 py-2 text-xs text-danger">{historyError}</p>
                  ) : history === null ? (
                    <p className="px-3 py-2 text-xs text-muted">Loading…</p>
                  ) : history.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted">No previous chats yet.</p>
                  ) : (
                    history.map((s) => {
                      const active = s.sessionId === sessionId;
                      return (
                        <div
                          key={s.sessionId}
                          className={
                            "flex items-start gap-0.5 pr-1 " + (active ? "bg-slate-50" : "")
                          }
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => void handleLoadSession(s.sessionId)}
                            className="flex min-w-0 flex-1 flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-slate-50"
                          >
                            <span className="line-clamp-2 text-sm font-medium text-ink">
                              {sessionTitle(s)}
                            </span>
                            <span className="text-xs text-muted">
                              {sessionScope(s)}
                              {s.updatedAt ? ` · ${formatSessionWhen(s.updatedAt)}` : ""}
                            </span>
                          </button>
                          <RemoveRowButton
                            label={`Delete chat: ${sessionTitle(s)}`}
                            disabled={deletingId === s.sessionId || isStreaming}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDeleteSession(s.sessionId);
                            }}
                            className="mt-1.5"
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Copy conversation"
            title="Copy conversation"
            disabled={!hasTranscript}
            onClick={() => void handleCopyConversation()}
            className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Copy size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={maximized ? "Restore AI assistant size" : "Expand AI assistant"}
            title={maximized ? "Restore" : "Expand"}
            aria-pressed={maximized}
            onClick={() => setMaximized((m) => !m)}
            className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          >
            {maximized ? (
              <Minimize2 size={16} aria-hidden="true" />
            ) : (
              <Maximize2 size={16} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            aria-label="Close AI assistant"
            onClick={onClose}
            className="rounded-lg p-1 text-muted hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        ref={transcriptRef}
        className={
          "flex-1 space-y-3 overflow-y-auto p-3 " + (maximized ? "sm:p-5" : "")
        }
      >
        {messages.length === 0 && !isStreaming ? (
          <p className="text-sm text-muted">
            Ask me to add items, reorganize a list, template, or checklist, or build something
            new. I will always show you the change before anything is saved.
          </p>
        ) : null}
        {messages.map((m) => (
          <div key={m.id}>
            <div
              data-reply={m.role === "assistant" ? "" : undefined}
              className={
                "group relative rounded-xl px-3 py-2 pr-10 text-sm " +
                (m.role === "user"
                  ? "ml-6 whitespace-pre-wrap bg-brand-600 text-white"
                  : "mr-6 bg-slate-100 text-ink")
              }
            >
              {m.role === "user" ? (
                m.text
              ) : m.text ? (
                <div data-reply-body="">
                  <ReplyMarkdown text={m.text} />
                </div>
              ) : null}
              {m.stopped ? (
                <p className={"text-xs italic text-muted" + (m.text ? " mt-1" : "")}>Stopped.</p>
              ) : null}
              {m.text ? (
                <button
                  type="button"
                  onClick={(e) => void handleCopyMessage(m, e.currentTarget)}
                  aria-label={m.role === "user" ? "Copy message" : "Copy reply"}
                  title="Copy"
                  className={
                    "absolute right-2 top-2 rounded-md p-1 opacity-0 transition hover:bg-black/10 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current group-hover:opacity-100 " +
                    (m.role === "user" ? "text-white" : "text-muted hover:text-ink")
                  }
                >
                  <Copy size={14} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            {m.role === "assistant" && m.seq !== undefined ? (
              <div className="mr-6 mt-1 flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Good response"
                  aria-pressed={m.rating === "up"}
                  disabled={Boolean(m.rating)}
                  onClick={() => handleRate(m, "up")}
                  className="rounded-md p-1 text-muted transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-default"
                >
                  <ThumbsUp size={13} className={m.rating === "up" ? "fill-brand-600 text-brand-600" : ""} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Bad response"
                  aria-pressed={m.rating === "down"}
                  disabled={Boolean(m.rating)}
                  onClick={() => handleRate(m, "down")}
                  className="rounded-md p-1 text-muted transition hover:bg-slate-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-default"
                >
                  <ThumbsDown size={13} className={m.rating === "down" ? "fill-danger text-danger" : ""} aria-hidden="true" />
                </button>
              </div>
            ) : null}
            {m.role === "assistant" && m.debug ? <AiDebugStrip debug={m.debug} /> : null}
            {m.cards?.map((card, i) => (
              <ChatCardView
                key={card.type === "ticket" ? card.cardId : `link-${i}`}
                card={card}
                submitted={card.type === "ticket" && submittedTicketIds.has(card.cardId)}
                onReviewTicket={handleReviewTicket}
              />
            ))}
          </div>
        ))}
        {isStreaming && streamingText ? (
          <div className="mr-6 rounded-xl bg-slate-100 px-3 py-2 text-sm text-ink">
            <ReplyMarkdown text={streamingText} />
          </div>
        ) : null}
        {isStreaming &&
          liveCards.map((card, i) => (
            <ChatCardView
              key={card.type === "ticket" ? card.cardId : `live-link-${i}`}
              card={card}
              submitted={card.type === "ticket" && submittedTicketIds.has(card.cardId)}
              onReviewTicket={handleReviewTicket}
            />
          ))}
        {isStreaming && indicatorLabel ? <TypingDots label={indicatorLabel} /> : null}
        {undo ? (
          <ProposalCard
            key={`undo-${undo.proposalId}`}
            proposalId={undo.proposalId}
            summary={undo.summary}
            ops={undo.ops}
            undoState={undo.state}
            onUndo={() => handleUndoRedo("undo")}
            onRedo={() => handleUndoRedo("redo")}
            onApplied={() => undefined}
            onDiscarded={() => undefined}
          />
        ) : null}
        {pendingProposal && pendingProposal.proposalId !== undo?.proposalId ? (
          <ProposalCard
            key={pendingProposal.proposalId}
            proposalId={pendingProposal.proposalId}
            summary={pendingProposal.summary}
            ops={pendingProposal.ops}
            onApplied={handleProposalApplied}
            onDiscarded={handleProposalDiscarded}
          />
        ) : null}
      </div>

      {error ? <div className="border-t border-line px-3 py-2 text-sm text-danger">{error}</div> : null}

      <div className="border-t border-line p-3">
        {effectiveDegradedReason ? (
          <div className="space-y-3">
            <p className="text-sm text-ink">{degradedSentence(effectiveDegradedReason)}</p>
            <SearchInput
              value={kbQuery}
              onChange={setKbQuery}
              onDebouncedChange={(q) => void runKbSearch(q)}
              placeholder="Search help notes…"
              aria-label="Search help notes"
            />
            {kbSection ? (
              <div className="rounded-xl border border-line bg-slate-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-ink">{kbSection.title}</p>
                  <button
                    type="button"
                    onClick={() => setKbSection(null)}
                    className="shrink-0 text-xs font-medium text-muted hover:text-ink"
                  >
                    Back to results
                  </button>
                </div>
                <p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap text-sm text-ink">
                  {kbSection.body}
                </p>
                {kbSection.routes[0] ? (
                  <Link
                    to={kbSection.routes[0]}
                    className="mt-2 inline-block text-sm font-medium text-brand-700 no-underline hover:underline"
                  >
                    Go there
                  </Link>
                ) : null}
              </div>
            ) : kbResults && kbResults.length > 0 ? (
              <ul className="max-h-40 space-y-1 overflow-y-auto">
                {kbResults.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => void openKbSection(r.id)}
                      className="w-full rounded-lg border border-line bg-white px-3 py-2 text-left hover:bg-slate-50"
                    >
                      <span className="block text-sm font-medium text-ink">{r.title}</span>
                      <span className="mt-0.5 block text-xs text-muted">{r.snippet}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : kbResults && kbResults.length === 0 ? (
              <p className="text-xs text-muted">No matching help notes.</p>
            ) : null}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() =>
                  setReportDialog({ surfaceId: "assistant.member.degraded", payload: { assistantProfile: "member" } })
                }
                className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700"
              >
                Report a problem
              </button>
              <Link to="/contact" className="text-sm font-medium text-brand-700 no-underline hover:underline">
                Contact us
              </Link>
            </div>
          </div>
        ) : turnLimitHit ? (
          <div className="space-y-3">
            <p className="text-sm text-ink">This conversation is at its limit. Start a new one to keep going.</p>
            <button
              type="button"
              onClick={handleNewChat}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700"
            >
              Start a new conversation
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              ref={composerRef}
              value={composerValue}
              onChange={(e) => setComposerValue(e.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder="Ask the assistant…"
              rows={2}
              disabled={isStreaming}
              className="min-h-[3.75rem] flex-1 resize-none rounded-xl border border-line bg-slate-50 px-3 py-2 text-sm leading-5 text-ink placeholder:text-muted transition focus-visible:border-brand-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 disabled:bg-slate-50"
            />
            {isStreaming ? (
              <button
                type="button"
                onClick={handleAbort}
                aria-label="Stop generating"
                title="Stop generating"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
              >
                <Square size={15} fill="currentColor" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={!composerValue.trim()}
                aria-label="Send message"
                title="Send message"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm transition hover:bg-brand-700 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
              >
                <ArrowUp size={19} strokeWidth={2.5} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <p className="mt-2 text-[11px] leading-snug text-muted-sm">
          {transcriptNotice(status.transcriptMode ?? "full")}
        </p>
      </div>

      <ReportProblemDialog
        open={reportDialog !== null}
        onClose={() => setReportDialog(null)}
        surfaceId={reportDialog?.surfaceId}
        payload={reportDialog?.payload}
        initialType={reportDialog?.initialType}
        prefillBody={reportDialog?.prefillBody}
        onSubmitted={reportDialog?.onSubmitted}
      />
    </div>
  );
}
